/**
 * 首尾帧上传器
 * 必须同时提供首帧和尾帧
 */

import { useRef, useState } from 'react';
import { Plus, X, FolderOpen } from 'lucide-react';
import { ImageLibraryPicker } from './ImageLibraryPicker';
import { deriveThumbnailUrl } from '../../../utils/imageDisplay';
import { withAuthToken } from '../../../utils/storageUrl';

interface VideoFirstLastFrameUploaderProps {
  firstFrame: string;
  lastFrame: string;
  onFirstFrameChange: (value: string) => void;
  onLastFrameChange: (value: string) => void;
  maxSizeMB?: number;
}

export function VideoFirstLastFrameUploader({
  firstFrame,
  lastFrame,
  onFirstFrameChange,
  onLastFrameChange,
  maxSizeMB = 10,
}: VideoFirstLastFrameUploaderProps) {
  return (
    <div>
      <div className="text-sm font-medium text-light-text mb-2">首尾帧</div>
      <div className="grid grid-cols-2 gap-2">
        <FrameSlot
          label="首帧"
          value={firstFrame}
          onChange={onFirstFrameChange}
          maxSizeMB={maxSizeMB}
        />
        <FrameSlot
          label="尾帧"
          value={lastFrame}
          onChange={onLastFrameChange}
          maxSizeMB={maxSizeMB}
        />
      </div>
      <p className="text-xs text-light-muted mt-1.5">
        首尾帧建议尺寸一致，单张 ≤ {maxSizeMB}MB
      </p>
    </div>
  );
}

interface FrameSlotProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  maxSizeMB: number;
}

function FrameSlot({ label, value, onChange, maxSizeMB }: FrameSlotProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`图片超过 ${maxSizeMB}MB`);
      return;
    }
    const dataUrl = await readAsDataUrl(file);
    onChange(dataUrl);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handlePickFromLibrary = (urls: string[]) => {
    if (urls.length > 0) onChange(urls[0]);
  };

  return (
    <div>
      <div className="text-xs text-light-muted mb-1">{label}</div>
      {value ? (
        <div className="relative aspect-square rounded-xl overflow-hidden border border-light-border bg-light-hover">
          <img src={renderImageSrc(value)} alt={label} className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="aspect-square w-full rounded-xl border-2 border-dashed border-light-border bg-white flex flex-col items-center justify-center gap-1.5 text-light-muted">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-light-border hover:border-accent-blue hover:text-accent-blue text-xs"
          >
            <Plus className="w-3.5 h-3.5" />
            上传{label}
          </button>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-light-border hover:border-accent-blue hover:text-accent-blue text-xs"
          >
            <FolderOpen className="w-3.5 h-3.5" />
            从图库选
          </button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleSelect(e.target.files)}
        className="hidden"
      />
      <ImageLibraryPicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickFromLibrary}
        maxSelect={1}
        title={`选择${label}`}
      />
    </div>
  );
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('读取失败'));
    reader.readAsDataURL(file);
  });
}

function renderImageSrc(src: string): string {
  const previewSrc = deriveThumbnailUrl(src, 'small') || src;
  if (previewSrc.startsWith('data:')) return previewSrc;
  if (previewSrc.includes('/api/')) return withAuthToken(previewSrc);
  return previewSrc;
}
